import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, SectionCard, StatCard, Button } from "@/components/PageUI";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { FileBarChart, DollarSign, Percent, Download } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const { club } = useAuth();
  const [finance, setFinance] = useState(null);
  const [teams, setTeams] = useState([]);
  const [attData, setAttData] = useState([]);
  const [dl, setDl] = useState("");

  const downloadPdf = async (type) => {
    setDl(type);
    try {
      const res = await fetch(`${API}/reports/pdf/${type}`, {
        headers: { ...(localStorage.getItem("cs_token") ? { Authorization: `Bearer ${localStorage.getItem("cs_token")}` } : {}) },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${type}-report.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate PDF"); } finally { setDl(""); }
  };

  useEffect(() => {
    api.get("/reports/financial").then((r) => setFinance(r.data));
    api.get("/teams").then(async (r) => {
      setTeams(r.data);
      const rows = [];
      for (const t of r.data) {
        const s = await api.get(`/attendance/summary?team_id=${t.id}`);
        const avg = s.data.length ? Math.round(s.data.reduce((a, x) => a + x.percentage, 0) / s.data.length) : 0;
        rows.push({ name: t.name, attendance: avg });
      }
      setAttData(rows);
    });
  }, []);

  if (!finance) return <Loader />;
  const cur = club?.currency || "USD";
  const byType = Object.entries(finance.by_type || {}).map(([k, v]) => ({ name: k, value: v }));
  const colors = ["#007AFF", "#14B8A6", "#f59e0b", "#a855f7", "#ef4444"];

  return (
    <div className="animate-fade-up">
      <PageHeader title="Reports" subtitle="Attendance, performance and financial insights — download polished PDFs." testid="reports-header"
        action={
          <div className="flex flex-wrap gap-2">
            {[["attendance", "Attendance"], ["performance", "Performance"], ["financial", "Financial"]].map(([t, l]) => (
              <Button key={t} size="sm" variant="outline" onClick={() => downloadPdf(t)} disabled={dl === t} data-testid={`pdf-${t}-btn`}>
                <Download size={14} className="mr-1.5" /> {dl === t ? "..." : l}
              </Button>
            ))}
          </div>
        } />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard icon={DollarSign} label="Total collected" value={`${cur} ${finance.total_collected.toFixed(2)}`} testid="report-collected" />
        <StatCard icon={Percent} label="Platform fees" value={`${cur} ${finance.platform_fees.toFixed(2)}`} accent="text-secondary" testid="report-fees" />
        <StatCard icon={FileBarChart} label="Transactions" value={finance.count} testid="report-count" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <SectionCard title="Attendance by team" testid="report-attendance-chart">
          {attData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={attData}>
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} domain={[0, 100]} />
                <Bar dataKey="attendance" radius={[6, 6, 0, 0]}>
                  {attData.map((_, i) => <Cell key={i} fill="#007AFF" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">No attendance data yet.</p>}
        </SectionCard>

        <SectionCard title="Revenue by fee type" testid="report-revenue-chart">
          {byType.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byType} layout="vertical">
                <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {byType.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">No revenue recorded yet.</p>}
        </SectionCard>
      </div>
    </div>
  );
}
